import * as React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button, Card, Logo } from "@chotca/ui";
import { getSdk } from "@chotca/api-client";
import { errMsg } from "./auth-errors";

export function VerifyEmailPage() {
  const t = useTranslations("auth");
  const [sp] = useSearchParams();
  const token = sp.get("token") ?? "";
  const [state, setState] = React.useState<
    { kind: "loading" } | { kind: "ok" } | { kind: "error"; message: string }
  >(() =>
    token
      ? { kind: "loading" }
      : { kind: "error", message: t("verifyEmailFailed") },
  );

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        await getSdk().auth.confirmEmailVerify({ token });
        if (!cancelled) setState({ kind: "ok" });
      } catch (err) {
        if (!cancelled)
          setState({
            kind: "error",
            message: errMsg(err, t("verifyEmailFailed")),
          });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const ok = state.kind === "ok";
  const loading = state.kind === "loading";

  return (
    <main className="mx-auto flex min-h-screen max-w-md flex-col justify-center px-6 py-10">
      <Link to="/" className="mb-8 flex items-center gap-2">
        <Logo variant="square" size={36} />
        <span className="text-title-md font-bold text-ink">Chốt Ca</span>
      </Link>
      <Card className="overflow-hidden p-0">
        <div className="flex flex-col items-center px-6 pt-8 pb-6 text-center">
          <div
            className={
              ok
                ? "flex h-16 w-16 items-center justify-center rounded-full bg-lime/20"
                : "flex h-16 w-16 items-center justify-center rounded-full bg-danger/10"
            }
          >
            {ok ? (
              <CheckCircle2
                className="h-9 w-9 text-lime-dark"
                strokeWidth={2.2}
              />
            ) : loading ? null : (
              <XCircle className="h-9 w-9 text-danger" strokeWidth={2.2} />
            )}
          </div>
          <h1 className="mt-5 text-display-md font-extrabold text-ink">
            {ok
              ? t("verifyEmailSuccess")
              : loading
                ? t("verifyEmailTitle")
                : t("verifyEmailTitle")}
          </h1>
          <p className="mt-2 max-w-sm text-body-md text-ash">
            {ok
              ? "Bạn đã có thể tiếp tục sử dụng đầy đủ tính năng của Chốt Ca."
              : loading
                ? "Đang xác nhận…"
                : state.kind === "error"
                  ? state.message
                  : ""}
          </p>
        </div>
        <div className="border-t border-line bg-mist/40 px-6 py-5">
          {ok ? (
            <Button
              fullWidth
              onClick={() => window.location.assign("/dashboard")}
            >
              Vào trang chủ
            </Button>
          ) : (
            <div className="flex flex-col gap-2">
              <Button
                fullWidth
                onClick={() => window.location.assign("/login")}
              >
                Đăng nhập để gửi lại
              </Button>
            </div>
          )}
        </div>
      </Card>
    </main>
  );
}
